import '../../styles/PageCommon.css';
import './InventoryHealthPage.css';
import SegmentedControl from '../../components/common/SegmentedControl/SegmentedControl';
import {
  Chart as ChartJS,
  LinearScale,
  Tooltip,
  Legend
} from "chart.js";
import { Chart as ReactChart } from "react-chartjs-2";
import { TreemapController, TreemapElement } from 'chartjs-chart-treemap';
import { useState, useEffect, useMemo } from 'react';
import InfoTooltip from '../../components/common/InfoTooltip/InfoTooltip';
import { inventoryHealthService } from '../../services/inventoryHealthService';
import LoadingAnimation from '../../components/common/LoadingAnimation/LoadingAnimation';
import { formatInputDateMonth, numberToKiloFormat } from '../../utils/utils';
import { NotificationContainer } from '../../components/common/NotificationContainer';
import NotificationModal from '../../components/common/NotificationModal';
import { parseApiError } from '../../utils/parseErrorMessage';
import Pagination from '../../components/common/Pagination/Pagination';
import { useFilterParams } from '../../hooks/useFilterParams';
import DashboardCard from '../../components/common/DashboardCard/DashboardCard';
import MultiSelectDropdown from '../../components/common/MultiSelectDropdown';
import MonthYearPicker from '../../components/common/MonthYearPicker';
import ColumnFilterDropdown from '../../components/common/ColumnFilterDropdown';

ChartJS.register(
  LinearScale,
  TreemapController,
  TreemapElement,
  Tooltip,
  Legend,
);

const externalTreeTooltipHandler = ({ chart, tooltip }) => {
  let tooltipEl = document.getElementById('tree-chart-tooltip');

  if (!tooltipEl) {
    tooltipEl = document.createElement('div');
    tooltipEl.id = 'tree-chart-tooltip';
    Object.assign(tooltipEl.style, {
      position: 'fixed',
      background: 'rgba(0,0,0,1)',
      color: '#fff',
      padding: '8px 12px',
      borderRadius: '16px',
      pointerEvents: 'none',
      zIndex: '9999',
      lineHeight: '1.5',
      maxWidth: '220px',
      transition: 'opacity 0.1s',
    });
    document.body.appendChild(tooltipEl);
  }

  if (!tooltip || tooltip.opacity === 0 || !tooltip.dataPoints?.length) {
    tooltipEl.style.opacity = '0';
    return;
  }

  const raw = tooltip.dataPoints[0].raw;
  const title = raw?._data?.label ?? raw?.label ?? '';
  const value = raw?._data?.v ?? raw?.v ?? '';

  tooltipEl.innerHTML = `
    <div style="font-weight:600;margin-bottom:2px;word-break:break-word;font-size:14px">${title}</div>
    <div style="font-size:12px;font-weight: 400, opacity: 0.7">${value}</div>
  `;
  tooltipEl.style.opacity = '1';

  const GAP = 10;
  const EDGE_PAD = 30;
  const rect = chart.canvas.getBoundingClientRect();
  const tw = tooltipEl.offsetWidth;
  const th = tooltipEl.offsetHeight;
  const vw = window.innerWidth;
  const vh = window.innerHeight;

  const cursorX = chart._lastEvent?.x ?? tooltip.caretX;
  const cursorY = chart._lastEvent?.y ?? tooltip.caretY;

  let x = rect.left + cursorX - tw / 2;
  let y = rect.top + cursorY - th - GAP;

  if (x + tw > vw - EDGE_PAD) x = vw - EDGE_PAD - tw;
  if (x < EDGE_PAD) x = EDGE_PAD;
  if (y < EDGE_PAD) y = rect.top + cursorY + GAP;
  if (y + th > vh - EDGE_PAD) y = vh - EDGE_PAD - th;

  tooltipEl.style.left = `${x}px`;
  tooltipEl.style.top = `${y}px`;
};

const COLUMNS = [
  { key: 'sku_code',      label: 'СКЮ код' },
  { key: 'sku_name',      label: 'Наименование товара' },
  { key: 'abc_category',  label: 'Категория' },
  { key: 'sales_value',   label: 'Продажи' },
  { key: 'share_percent', label: 'Доля' },
  { key: 'health_index',  label: 'Индекс Здоровья' },
];

const DEFAULT_FILTERS = {
  page: 1,
  page_size: 50,
  branch: [],
  period: formatInputDateMonth(new Date(new Date().setMonth(new Date().getMonth()))),
  view_type: "dsp",
  ...Object.fromEntries(COLUMNS.map(({ key }) => [key, []])),
};

export default function InventoryHealthPage() {
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [dataError, setDataError] = useState(null);
  const [inventoryTable, setInventoryTable ] = useState([]);
  const [inventoryTableTotalItems, setinventoryTableTotalItems] = useState(null);
  const [availableDateRange, setAvailableDateRange] = useState(null);

  const [branches, setBranches] = useState([]);
  const [tableFilterOptions, setTableFilterOptions] = useState({});
  const [categoryA, setCategoryA ] = useState(null)
  const [categoryB, setCategoryB ] = useState(null)
  const [categoryC, setCategoryC ] = useState(null)
  const [overstockData, setOverstockData] = useState([]);
  const [understockData, setUnderstockData] = useState([]);
  const [stockoutData, setStockoutData] = useState([]);
  const [deviationInfo, setDeviationInfo] = useState(null);

  // --- Filters State and Logic --- 
  const { filters, updateFilters } = useFilterParams(DEFAULT_FILTERS);

  // True once we have the date range and the selected period is within bounds.
  // Blocks data fetches until init settles (including any period correction).
  const canFetchData = availableDateRange != null && filters.period <= availableDateRange.maxDate;

  useEffect(() => {
    return () => {
      const el = document.getElementById('tree-chart-tooltip');
      if (el) el.remove();
    };
  }, []);

  // Phase 1: resolve branches, date range, and correct period if out of range.
  // The `cancelled` flag handles React StrictMode's double-invocation in dev.
  useEffect(() => {
    let cancelled = false;
    inventoryHealthService.getFilerOptions(filters.period)
      .then(res => {
        if (cancelled) return;
        if (res.branch_names) setBranches(res.branch_names);
        if (res.max_date && res.min_date) setAvailableDateRange({ minDate: res.min_date, maxDate: res.max_date });
        if (res.max_date && filters.period > res.max_date) updateFilters({ period: res.max_date });
      })
      .catch(() => {})
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.period]);

  // Phase 2a: table data — re-fetches on any filter change once init is done.
  useEffect(() => {
    if (!canFetchData) return;
    let cancelled = false;
    setIsLoadingData(true);
    const columnFilters = Object.fromEntries(
      COLUMNS.map(({ key }) => [key, filters[key]]).filter(([, v]) => v?.length)
    );
    inventoryHealthService.getTableData(
      filters.page, filters.view_type, filters.branch, filters.period, filters.page_size, columnFilters
    ).then(res => {
      if (cancelled) return;
      if (res.total_items) setinventoryTableTotalItems(res.total_items);
      if (res.items) setInventoryTable(res.items);
      if (res.filter_options) setTableFilterOptions(res.filter_options);
    }).catch(error => {
      if (!cancelled) setDataError(parseApiError(error) || "");
    }).finally(() => { if (!cancelled) setIsLoadingData(false); });
    return () => { cancelled = true; };
  }, [canFetchData, filters]);

  // Phase 2b: health data — re-fetches when view_type, branch, or period changes.
  useEffect(() => {
    if (!canFetchData) return;
    let cancelled = false;
    setIsLoadingData(true);
    Promise.allSettled([
      inventoryHealthService.getCategoryA(filters.view_type, filters.branch, filters.period),
      inventoryHealthService.getCategoryB(filters.view_type, filters.branch, filters.period),
      inventoryHealthService.getCategoryC(filters.view_type, filters.branch, filters.period),
      inventoryHealthService.getOverstock(filters.view_type, filters.branch, filters.period),
      inventoryHealthService.getUnderstock(filters.view_type, filters.branch, filters.period),
      inventoryHealthService.getOutofStock(filters.view_type, filters.branch, filters.period),
      inventoryHealthService.getHealthDeviationInfo(),
    ]).then(([catA, catB, catC, overstock, understock, outofstock, deviation]) => {
      if (cancelled) return;
      if (catA.status === 'fulfilled' && catA.value) setCategoryA(catA.value);
      if (catB.status === 'fulfilled' && catB.value) setCategoryB(catB.value);
      if (catC.status === 'fulfilled' && catC.value) setCategoryC(catC.value);
      if (overstock.status === 'fulfilled' && overstock.value?.items) setOverstockData(overstock.value.items);
      if (understock.status === 'fulfilled' && understock.value?.items) setUnderstockData(understock.value.items);
      if (outofstock.status === 'fulfilled' && outofstock.value?.items) setStockoutData(outofstock.value.items);
      if (deviation.status === 'fulfilled' && deviation.value) setDeviationInfo(deviation.value);
      const err = [catA, catB, catC, overstock, understock, outofstock, deviation].find(r => r.status === 'rejected');
      if (err) setDataError(parseApiError(err.reason) || "");
    }).finally(() => { if (!cancelled) setIsLoadingData(false); });
    return () => { cancelled = true; };
  }, [canFetchData, filters.view_type, filters.branch, filters.period]);

  const colOptions = useMemo(() => {
    const result = {};
    Object.entries(tableFilterOptions).forEach(([key, values]) => {
      result[key] = (values || []).map(String);
    });
    return result;
  }, [tableFilterOptions]);

  const handleColFilterApply = (key, vals) => updateFilters({ [key]: vals, page: 1 });
  const getColSelected = (key) => filters[key] || [];

  const generateColors = (count) => {
    const saturation = 70;
    const lightness = 55;
    const opacity = 0.7;

    return Array.from({ length: count }, (_, i) => {
      const hue = (i * 137.508) % 360;

      // const saturation = 60 + (i % 3) * 10; // 60, 70, 80
      // const lightness = 50 + (i % 2) * 10;  // 50, 60
      
      return `hsla(${hue}, ${saturation}%, ${lightness}%, ${opacity})`;
    });
  };


  const overstockChartTreeData = {
    datasets: [{
      label: '',
      tree: overstockData.map((item, index) => ({
        v: item.share_of_stock,
        label: item.sku_name,
        color: generateColors(overstockData.length)[index]
      })),
      key: 'v',
      spacing: 2,
      borderColor: "#ffffff",
      borderRadius: 4,
      backgroundColor: (ctx) => ctx?.raw?._data.color,
      labels: {
        display: true,
        formatter: (ctx) => ctx.raw.label,
      },
    }],
  };
  const undestockChartTreeData = {
    datasets: [{
      label: '',
      tree: understockData.map((item, index) => ({
        v: item.share_of_stock,
        label: item.sku_name,
        color: generateColors(understockData.length)[index]
      })),
      key: 'v',
      spacing: 2,
      borderColor: "#ffffff",
      borderRadius: 4,
      backgroundColor: (ctx) => ctx?.raw?._data.color,
      labels: {
        display: true,
        formatter: (ctx) => ctx.raw.label,
      },
    }],
  };


  const treeMapOptions = {
    plugins: {
      legend: { display: false },
      tooltip: {
        position: 'average', 
        enabled: false,
        external: externalTreeTooltipHandler,
      },
    },
  };

  // eslint-disable-next-line no-unused-vars
  const overstockChartData = {
    labels: overstockData.map(item => item.sku_name),
    datasets: [
      {
        data: overstockData.map(item => item.share_of_stock),
        spacing: 0,
        borderRadius: overstockData.length > 1 ? 4 : 0,
        borderWidth: overstockData.length > 1 ? 2 : 0,
        borderColor: "#ffffff",
        backgroundColor: generateColors(overstockData.length),
        hoverOffset: 2,
      },
    ],
  };
  // eslint-disable-next-line no-unused-vars
  const undestockChartData = {
    labels: understockData.map(item => item.sku_name),
    datasets: [
      {
        data: understockData.map(item => item.share_of_stock),
        spacing: 0,
        borderRadius: understockData.length > 1 ? 4 : 0,
        borderWidth: understockData.length > 1 ? 2 : 0,
        borderColor: "#ffffff",
        backgroundColor: generateColors(understockData.length),
        hoverOffset: 2,
      },
    ],
  };
  const stockoutFiltered = stockoutData.filter(item => item.share_of_stock > 0);
  // eslint-disable-next-line no-unused-vars
  const stockoutChartData = {
    labels: stockoutFiltered.map(item => item.sku_name),
    datasets: [
      {
        data: stockoutFiltered.map(item => item.share_of_stock),
        spacing: 0,
        borderRadius: stockoutFiltered.length > 1 ? 4 : 0,
        borderWidth: stockoutFiltered.length > 1 ? 2 : 0,
        borderColor: "#ffffff",
        backgroundColor: generateColors(stockoutFiltered.length),
        hoverOffset: 2,
      },
    ],
  };

  // const donutChartOptions = {
  //   responsive: true,
  //   cutout: "45%",
  //   maintainAspectRatio: false,
  //   padding: 10,
  //   plugins: {
  //     legend: {
  //       display: false,
  //     },
  //     tooltip: {
  //       enabled: false,
  //       external: externalTooltipHandler,
  //     },
  //   },
  // };
  

  const sortedCategories = [categoryA?.sales_share_percent ?? 0,
    categoryB?.sales_share_percent ?? 0,
    categoryC?.sales_share_percent ?? 0,
  ].sort((a, b) => a - b);



  return (
    <>
      {isLoadingData && <LoadingAnimation />}
      <div className="page-content">
        <NotificationContainer>
          {dataError && <NotificationModal closeModal={() => setDataError(null)} type="error" subTitle={dataError} title="Ошибка" duration={4000}/>}
        </NotificationContainer>
        <div className="inventory-header-container">
          <h1 className="inventory-title">Индекс здоровья товарного запаса</h1>
          <div className="inventory-filters">
            <SegmentedControl
              value={filters.view_type?.toLowerCase() ?? "dsp"}
              onChange={(value) => updateFilters({ view_type: value })}
              options={[
                {
                  label: "Деньги",
                  options: [
                    { label: "Прайс-лист", value: "dsp" },
                    { label: "Инвойс цена", value: "invoice price" },
                  ],
                },
                { label: "Короб", value: "cases" },
              ]}
            />
            <MultiSelectDropdown
              label="Склад"
              placeholder="Все"
              selectedOptions={filters.branch}
              options={branches}
              onChange={(values) => {console.log("values ", values); updateFilters({branch: values})}}
              applySidebarAlignment={true}
            />
              
            <MonthYearPicker
              value={filters.period}
              onChange={(val) => updateFilters({ period: val })}
              placeholder="-"
              disableFuture={true}
              minDate={availableDateRange?.minDate}
              maxDate={availableDateRange?.maxDate}
            />
          </div>
        </div>

        <div className='inventory-health-body'>
          <div className="dashboard-cards-item-row">
              <DashboardCard cardData={categoryA} viewType={filters.view_type}
                  color={sortedCategories[2] === categoryA?.sales_share_percent ? "green" :
                    sortedCategories[1] === categoryA?.sales_share_percent ? "orange" : "red"}
              />
              <DashboardCard cardData={categoryB} viewType={filters.view_type}
                  color={sortedCategories[2] === categoryB?.sales_share_percent ? "green" :
                    sortedCategories[1] === categoryB?.sales_share_percent ? "orange" : "red"}
              />
              <DashboardCard cardData={categoryC} viewType={filters.view_type}
                  color={sortedCategories[2] === categoryC?.sales_share_percent ? "green" :
                    sortedCategories[1] === categoryC?.sales_share_percent ? "orange" : "red"}
              />
          </div>

          <div style={{display: 'flex', justifyContent: 'space-between', gap: "16px"}}>            

            <div className='inventory-health-data-table-container'>
              <div className="data-table-container" >
                <table className="data-table inventory-health-data-table" style={{minWidth: "auto"}}>
                  <thead>
                    <tr>
                      {COLUMNS.map(({ key, label }) => {
                        const hasFilter = key in tableFilterOptions;
                        return (
                          <th key={key}>
                            <div style={{ display: 'flex', height: '100%', marginBottom: 'auto', gap: 6, }}>
                              <span>{label}</span>
                              {hasFilter && (
                                <ColumnFilterDropdown
                                  label={label}
                                  options={colOptions[key] || []}
                                  selected={getColSelected(key)}
                                  onApply={(vals) => handleColFilterApply(key, vals)}
                                />
                              )}
                            </div>
                          </th>
                        );
                      })}
                    </tr>
                  </thead>
                  <tbody>
                    {
                      (inventoryTable && inventoryTable.length > 0) ? inventoryTable.map((inventory, index) => (
                        <tr key={`${inventory.sku_code }-${index}`}>
                          <td>
                            {inventory.sku_code}
                          </td>
                          <td>
                            {inventory.sku_name}
                          </td>
                          <td>                      
                            <span className={`inv-health-table-cell-category-item ${inventory.abc_category === "A" ? "a-catg" : inventory.abc_category === "B" ? "b-catg" : "c-catg"}`}>
                              {inventory.abc_category}
                            </span>
                          </td>
                          <td style={{minWidth: "150px"}}>
                            {numberToKiloFormat(inventory.sales_value, { kiloThreshold: 1_000_000 }) + " ₸"}
                          </td>
                          <td>
                            {inventory.share_percent + "%"}
                          </td>
                          <td className="inv-health-table-column-invHealth">
                            {inventory.health_index}
                          </td>
                        </tr>
                      )) : <tr key={"inventoryHealth-1"} >
                              <td style={{margin: "auto", textAlign: 'center'}} colSpan="6">Нет данных</td>
                          </tr>
                    }
                  </tbody>
                </table>
                <Pagination
                  currentPage={filters.page}
                  totalItems={inventoryTableTotalItems}
                  onPageChange={(p) => updateFilters({page: p})}
                  rowsPerPage={filters.page_size}
                  onRowsPerPageChange={(r) => { updateFilters({page_size: r}) }}
                  wrapperStyle={{minWidth: "0px"}}
                  rowsPerPageOptions={[5, 10, 20, 50]}
                />
              </div>
            </div>

            <div className='inventory-health-donut-charts-container'>
              {
                overstockData && overstockData.length > 0 &&
                (
                  <div className='inventory-health-donut-chart-item' style={{borderBottom: "1px solid #E7E7E7"}}>
                    <div className='inventory-health-donut-chart-title' style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      Излишки: ТОП 5 СКЮ
                      {deviationInfo?.overstock_logic && (
                        <InfoTooltip approxHeight={240}>{deviationInfo.overstock_logic}</InfoTooltip>
                      )}
                    </div>
                    <div className='inventory-health-donut-chart-draw'>
                      <ReactChart style={{width: "100%"}} type="treemap" data={overstockChartTreeData} options={treeMapOptions}/>
                      {/* <Doughnut data={overstockChartData} options={donutChartOptions} /> */}
                    </div>
                    <div className='inventory-health-donut-chart-legends'>
                      <div className="legend-labels-container">
                        <div className="legend-labels">
                          {
                            overstockData.map((item, index) => (
                              <div key={index} className="legend-label-container">
                                <div className="legend-label-indicator" style={{backgroundColor: generateColors(overstockData.length)[index]}} />
                                <div style={{display: 'flex', width: "100%", gap: 12, justifyContent:'space-between', alignItems:'center'}}>
                                <span style={{
                                    whiteSpace: "break-spaces",
                                    overflow: "hidden",
                                    maxWidth: "200px",
                                  }}>
                                    {item.sku_name}
                                  </span>
                                  <span>{item.health_index_deviation} %</span>
                                </div>
                              </div>
                            ))
                          }
                        </div>
                      </div>
                    </div>
                  </div>
                )
              }

              {
                understockData && understockData.length > 0 &&
                (
                  <div className='inventory-health-donut-chart-item' style={{borderBottom: "1px solid #E7E7E7"}}>
                    <div className='inventory-health-donut-chart-title' style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      Дефицит: ТОП 5 СКЮ
                      {deviationInfo?.understock_logic && (
                        <InfoTooltip approxHeight={240}>{deviationInfo.understock_logic}</InfoTooltip>
                      )}
                    </div>
                    <div className='inventory-health-donut-chart-draw'>
                      <ReactChart style={{width: "100%"}} type="treemap" data={undestockChartTreeData} options={treeMapOptions}/>
                      {/* <Doughnut data={undestockChartData} options={donutChartOptions} /> */}
                    </div>
                    <div className='inventory-health-donut-chart-legends'>
                      <div className="legend-labels-container">
                        <div className="legend-labels">
                          {
                            understockData.map((item, index) => (
                              <div key={index} className="legend-label-container">
                                <div className="legend-label-indicator" style={{backgroundColor: generateColors(understockData.length)[index]}} />
                                <div style={{display: 'flex', width: "100%", gap: 12, justifyContent:'space-between', alignItems:'center'}}>
                                  <span style={{
                                    whiteSpace: "break-spaces",
                                    overflow: "hidden",
                                    maxWidth: "200px"
                                  }}>
                                    {item.sku_name}
                                  </span>
                                  <span>{item.health_index_deviation} %</span>
                                </div>
                              </div>
                            ))
                          }
                        </div>
                      </div>
                    </div>
                  </div>
                )
              }
              {
                stockoutData && stockoutData.length > 0 && 
                (
                  <div className='inventory-health-donut-chart-item'>
                    <div className='inventory-health-donut-chart-title' style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      Сток аут: ТОП 5 СКЮ
                      {deviationInfo?.out_of_stock_logic && (
                        <InfoTooltip approxHeight={240}>{deviationInfo.out_of_stock_logic}</InfoTooltip>
                      )}
                    </div>
                    {/* {
                      stockoutChartData.labels?.length > 0 &&
                        <div className='inventory-health-donut-chart-draw'>
                          <Doughnut data={stockoutChartData} options={donutChartOptions} />
                        </div>
                    } */}
                    <div className='inventory-health-donut-chart-legends'>
                      <div className="legend-labels-container">
                        <div className="legend-labels">
                          {
                            stockoutData.map((item, index) => (
                              <div key={index} className="legend-label-container">
                                <div className="legend-label-indicator" style={{backgroundColor: generateColors(stockoutData.length)[index]}} />
                                <div style={{display: 'flex', width: "100%", gap: 12, justifyContent:'space-between', alignItems:'center'}}>
                                  <span style={{
                                    whiteSpace: "break-spaces",
                                    overflow: "hidden",
                                    maxWidth: "200px"
                                  }}>
                                    {item.sku_name}
                                  </span>
                                  {/* <span>{item.average_historical_sales}</span> */}
                                </div>
                              </div>
                            ))
                          }
                        </div>
                      </div>
                    </div>
                  </div>
                )
              }
            </div>
          </div>
        </div>
      </div>

    </>
  );
}
